import { supabase } from "@/integrations/supabase/client";
import { logger } from "./logger";
import {
  CorporateCardRebateProgram,
  CorporateCardRebateProgramWithCorporate,
  CreateCardProgramRequest,
  UpdateCardProgramRequest,
  CorporateFinancialProfile,
  CorporatePaymentCost,
  CreateFinancialProfileRequest,
  CreatePaymentCostRequest,
  CardProgramVolumeSnapshot,
} from "@/types/CardProgram";
import {
  calculateRebateFromProgram,
  unmatchedRebate,
  getRebateRateForTier as _getRebateRateForTier,
  getConfiguredTierNames as _getConfiguredTierNames,
  getCurrentTierName,
} from "./cardProgram/rebate-calculation.service";
import type { RebateCalculationResult } from "./cardProgram/rebate-calculation.service";
import * as financialProfile from "./cardProgram/financial-profile.service";

export const cardProgramService = {
  // ==================== Card Program CRUD ====================

  async getCorporateCardPrograms(corporateId: number): Promise<CorporateCardRebateProgram[]> {
    if (!corporateId || !Number.isFinite(corporateId) || corporateId <= 0) {
      return [];
    }
    try {
      const { data, error } = await supabase
        .from('corporate_card_rebate_programs')
        .select('*')
        .eq('corporate_id', corporateId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(program => ({
        ...program,
        level_iii_qualification_requirements: (typeof program.level_iii_qualification_requirements === 'object' ? program.level_iii_qualification_requirements : {}) as Record<string, unknown>
      }));
    } catch (error: unknown) {
      logger.error('Error in cardProgramService.getCorporateCardPrograms', error);
      throw error;
    }
  },

  async getCorporateCardProgramWithCorporate(corporateId: number): Promise<CorporateCardRebateProgramWithCorporate | null> {
    try {
      const { data, error } = await supabase
        .from('corporate_card_rebate_programs')
        .select(`*, corporates!inner(name)`)
        .eq('corporate_id', corporateId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      return { ...data, corporate_name: data.corporates?.name || 'Unknown Corporate' } as CorporateCardRebateProgramWithCorporate;
    } catch (error: unknown) {
      logger.error('Error in cardProgramService.getCorporateCardProgramWithCorporate', error);
      throw error;
    }
  },

  async createCardProgram(program: CreateCardProgramRequest): Promise<CorporateCardRebateProgram> {
    try {
      // Prevent duplicate active programs — update if one already exists
      const existing = await this.getCorporateCardPrograms(program.corporate_id);
      if (existing.length > 0) {
        return this.updateCardProgram(existing[0].id, { ...program, program_name: program.program_name || 'Corporate Card Program' });
      }

      const { data, error } = await supabase
        .from('corporate_card_rebate_programs')
        .insert([{ ...program, program_name: program.program_name || 'Corporate Card Program' }])
        .select()
        .single();

      if (error) throw error;
      return { ...data, level_iii_qualification_requirements: (typeof data.level_iii_qualification_requirements === 'object' ? data.level_iii_qualification_requirements : {}) as Record<string, unknown> };
    } catch (error: unknown) {
      logger.error('Error in cardProgramService.createCardProgram', error);
      throw error;
    }
  },

  async updateCardProgram(programId: string, updates: UpdateCardProgramRequest): Promise<CorporateCardRebateProgram> {
    try {
      const { data, error } = await supabase
        .from('corporate_card_rebate_programs')
        .update(updates as never)
        .eq('id', programId)
        .select()
        .single();

      if (error) throw error;
      return { ...data, level_iii_qualification_requirements: (typeof data.level_iii_qualification_requirements === 'object' ? data.level_iii_qualification_requirements : {}) as Record<string, unknown> };
    } catch (error: unknown) {
      logger.error('Error in cardProgramService.updateCardProgram', error);
      throw error;
    }
  },

  // ==================== Tier & Rebate (delegated) ====================

  async getCurrentTier(corporateId: number, monthlyVolume: number): Promise<string | null> {
    try {
      const programs = await this.getCorporateCardPrograms(corporateId);
      if (!programs[0]) return null;
      return getCurrentTierName(programs[0], monthlyVolume);
    } catch (error: unknown) {
      logger.error('Error in cardProgramService.getCurrentTier', error);
      throw error;
    }
  },

  async updateCurrentTier(corporateId: number, currentTierName: string): Promise<void> {
    try {
      const programs = await this.getCorporateCardPrograms(corporateId);
      if (programs[0]) await this.updateCardProgram(programs[0].id, { current_tier_name: currentTierName });
    } catch (error: unknown) {
      logger.error('Error in cardProgramService.updateCurrentTier', error);
      throw error;
    }
  },

  async calculateRebate(
    corporateId: number,
    annualVolumeAmount: number,
    monthlyVolumeForTier: number,
    hasLevelIIIData: boolean = false,
    avgTransactionSize?: number
  ): Promise<RebateCalculationResult> {
    try {
      const programs = await this.getCorporateCardPrograms(corporateId);
      // No program configured is "cannot say", not "earns nothing".
      if (!programs[0]) return unmatchedRebate('no_active_program');
      return calculateRebateFromProgram(programs[0], annualVolumeAmount, monthlyVolumeForTier, hasLevelIIIData, avgTransactionSize);
    } catch (error: unknown) {
      logger.error('Error in cardProgramService.calculateRebate', error);
      throw error;
    }
  },

  getRebateRateForTier: _getRebateRateForTier,
  getConfiguredTierNames: _getConfiguredTierNames,

  // ==================== Financial Profiles (delegated) ====================

  getFinancialProfile: financialProfile.getFinancialProfile,
  getFinancialProfileHistory: financialProfile.getFinancialProfileHistory,
  createFinancialProfile: financialProfile.createFinancialProfile,
  updateFinancialProfile: financialProfile.updateFinancialProfile,
  getPaymentCosts: financialProfile.getPaymentCosts,
  createPaymentCost: financialProfile.createPaymentCost,
  updatePaymentCost: financialProfile.updatePaymentCost,
  upsertPaymentCost: financialProfile.upsertPaymentCost,

  // ==================== Volume Snapshots ====================

  async getVolumeSnapshots(corporateId: number, programId?: string, limit = 12): Promise<CardProgramVolumeSnapshot[]> {
    try {
      let query = supabase
        .from('card_program_volume_snapshots')
        .select('*')
        .eq('corporate_id', corporateId)
        .order('snapshot_month', { ascending: false })
        .limit(limit);

      if (programId) query = query.eq('card_program_id', programId);

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as CardProgramVolumeSnapshot[];
    } catch (error: unknown) {
      logger.error('Error fetching volume snapshots', error);
      throw error;
    }
  },

  async upsertVolumeSnapshot(snapshot: Omit<CardProgramVolumeSnapshot, 'id' | 'created_at' | 'updated_at'>): Promise<CardProgramVolumeSnapshot> {
    try {
      const { data, error } = await supabase
        .from('card_program_volume_snapshots')
        .upsert(snapshot, { onConflict: 'card_program_id,snapshot_month' })
        .select()
        .single();

      if (error) throw error;
      return data as CardProgramVolumeSnapshot;
    } catch (error: unknown) {
      logger.error('Error upserting volume snapshot', error);
      throw error;
    }
  }
};
